import React, { useState } from "react";
import { COMPONENT_TYPES } from "../../../../pages/constants/AiAgent";
import { ComponentActionButton } from "./ComponentActionButton";
import { useComponentAction } from "./useComponentAction";
import {
  HiOutlineBriefcase,
  HiOutlineMapPin,
  HiOutlineClock,
  HiOutlineUsers,
  HiOutlineBanknotes,
  HiOutlineComputerDesktop,
  HiOutlineDocumentText,
  HiOutlineCalendarDays,
  HiOutlineUser,
  HiOutlineLink,
} from "react-icons/hi2";

const DetailRow = ({ icon: Icon, label, value }) => {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2 text-xs">
      <Icon className="w-3.5 h-3.5 text-textGray mt-0.5 flex-shrink-0" />
      <div>
        <span className="text-textGray">{label}: </span>
        <span className="text-primaryBlack font-medium">{value}</span>
      </div>
    </div>
  );
};

const JobDetailCard = ({ payload, disabled }) => {
  const { data, actions } = payload;
  const { sendAction } = useComponentAction();
  const [actionsDisabled, setActionsDisabled] = useState(disabled);

  const handleAction = (action, params) => {
    setActionsDisabled(true);
    sendAction(COMPONENT_TYPES.JOB_DETAIL_CARD, action, params);
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white w-full">
      <h3 className="text-sm font-semibold text-primaryBlack">{data.title}</h3>

      <div className="flex flex-col gap-1.5 mt-3">
        <DetailRow icon={HiOutlineBriefcase} label="Department" value={data.department} />
        <DetailRow icon={HiOutlineMapPin} label="Location" value={data.location} />
        <DetailRow icon={HiOutlineClock} label="Experience" value={data.experience} />
        <DetailRow icon={HiOutlineComputerDesktop} label="Work Style" value={data.work_style} />
        <DetailRow icon={HiOutlineDocumentText} label="Job Type" value={data.job_type} />
        <DetailRow
          icon={HiOutlineUsers}
          label="Positions"
          value={data.positions ? `${data.positions} (${data.applicants ?? 0} applicants)` : null}
        />
        {data.salary_range && (
          <DetailRow icon={HiOutlineBanknotes} label="Salary" value={data.salary_range} />
        )}
      </div>

      {data.skills && (
        <div className="flex flex-wrap gap-1 mt-3">
          {data.skills.split(",").map((skill, i) => (
            <span key={i} className="text-[10px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">
              {skill.trim()}
            </span>
          ))}
        </div>
      )}

      {(data.created_at || data.recruiter_name) && (
        <div className="flex flex-col gap-1.5 mt-3 border-t border-gray-100 pt-2">
          {data.created_at && (
            <DetailRow icon={HiOutlineCalendarDays} label="Posted" value={data.created_at} />
          )}
          {data.recruiter_name && (
            <DetailRow icon={HiOutlineUser} label="Recruiter" value={data.recruiter_name} />
          )}
        </div>
      )}

      {data.job_link && (
        <a
          href={data.job_link}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 mt-2 text-xs text-blue-600 hover:text-blue-800 hover:underline"
        >
          <HiOutlineLink className="w-3.5 h-3.5" />
          View Job Posting
        </a>
      )}

      {actions?.length > 0 && (
        <div className="flex gap-2 mt-3 pt-2 border-t border-gray-100">
          {actions.map((action) => (
            <ComponentActionButton
              key={action.action}
              {...action}
              onAction={handleAction}
              disabled={actionsDisabled}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default JobDetailCard;
